// @givmo/app-admin — the onboarding trigger + the `/onboarding/match-policy` route component.
//
// Trigger (parity with the Flutter `_maybeRedirectToOnboarding` on the dashboard): a fresh admin is
// sent into the match-policy wizard when, and ONLY when —
//   (a) the company read has resolved,
//   (b) it has NO per-employee match budget (`companyNeedsOnboarding`), and
//   (c) the per-company dismissal flag is NOT set on this device.
// A company that already has a budget reaches the app normally — the wizard never blocks it.
//
// We mount the trigger as a redirect from the authenticated layout (`useOnboardingRedirect`) to a
// dedicated `/onboarding/match-policy` route rather than the (unbuilt 07-1.2) dashboard — the gate
// reads the company directly, depending on no dashboard screen. The dashboard feature is the
// Flutter trigger site, but here the layout is the stable host that every authenticated route
// shares. The route component itself re-checks the same gate so a manual navigation to
// `/onboarding/match-policy` by a budgeted/dismissed admin bounces straight back to the dashboard.

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { companiesGetCompanyOptions } from '@givmo/api-client/query';
import { useEmployerModulePaths } from '../../lib/module-paths';
import { companyNeedsOnboarding } from './onboarding-consts';
import { isOnboardingDismissed } from './onboarding-dismissal';
import { OnboardingWizard } from './onboarding-wizard';

// The wizard's address and the landing it leaves to are the HOST's (EP-MNT4's address book): the
// portal's `/onboarding/match-policy` → `/dashboard`, the console's `/donations/onboarding/match-policy`
// → `/donations`. In the console the redirect is mounted by the Donations module's own route wrapper,
// so it fires only inside the module — never on an Admin Console or Organization page.

/**
 * Layout-level effect: once the company read resolves, redirect a no-budget, not-dismissed admin
 * into the wizard. No-ops while already on the onboarding route (so the route owns the wizard) and
 * for any budgeted/dismissed company. Mounted once in the authenticated layout.
 */
export function useOnboardingRedirect(): void {
  const navigate = useNavigate();
  const { onboarding: onboardingPath } = useEmployerModulePaths();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const companyQuery = useQuery(companiesGetCompanyOptions());
  const company = companyQuery.data;

  useEffect(() => {
    if (company === undefined) return;
    // Never re-enter while the wizard route is mounted — it owns the flow from here.
    if (pathname.startsWith(onboardingPath)) return;
    if (!companyNeedsOnboarding(company)) return;
    if (isOnboardingDismissed(company.id)) return;
    void navigate({ to: onboardingPath, replace: true });
  }, [company, pathname, navigate, onboardingPath]);
}

/**
 * The `/onboarding/match-policy` route component. Loads the company, then either renders the
 * wizard (no-budget + not-dismissed) or bounces to the dashboard (budgeted OR dismissed OR a manual
 * navigation by a company that doesn't qualify) — so the wizard never blocks a budgeted admin.
 */
export function OnboardingMatchPolicyRoute() {
  const navigate = useNavigate();
  const { dashboard: landingPath } = useEmployerModulePaths();
  const companyQuery = useQuery(companiesGetCompanyOptions());
  const company = companyQuery.data;

  const leave = () => void navigate({ to: landingPath, replace: true });

  // A qualifying company is one with no budget and no dismissal flag. Anything else (incl. a manual
  // nav here by a budgeted/dismissed admin) leaves immediately.
  const qualifies =
    company !== undefined && companyNeedsOnboarding(company) && !isOnboardingDismissed(company.id);

  useEffect(() => {
    if (company !== undefined && !qualifies) {
      navigate({ to: landingPath, replace: true });
    }
  }, [company, qualifies, navigate, landingPath]);

  if (company === undefined) {
    return <div data-testid="onboarding-loading" />;
  }
  if (!qualifies) {
    // Redirect is in flight (effect above) — render nothing rather than the wizard.
    return null;
  }

  return <OnboardingWizard company={company} onLeave={leave} />;
}
